import { createSelector } from 'reselect';
import {
    transformPermissionsKeysToArray,
    transformArrayToObject,
} from './transformers';
import DefaultPic from '../../icons/defaultProfilePicture.png';

export const getAuthState = (state) => state.auth || {};

export const getLoginProgressState = (state) =>
    state.auth.loginInProgress || false;

export const getPaymentProgressState = (state) =>
    state.auth.paymentInProgress || false;

export const isUserLoggedIn = createSelector(
    getAuthState,
    (auth) => auth.isLoggedIn,
);

export const getUserCreds = createSelector(
    getAuthState,
    (auth) => auth.userCreds,
);

export const getAuthToken = createSelector(getAuthState, (auth) => auth.token);

export const getLoginError = createSelector(
    getAuthState,
    (auth) => auth.loginError,
);

export const getLoginProgress = createSelector(
    getLoginProgressState,
    (loading) => loading,
);

export const getPaymentError = createSelector(
    getAuthState,
    (auth) => auth.paymentError,
);

export const getSignUpError = createSelector(
    getAuthState,
    (auth) => auth.signUpError,
);

export const getActivePackgePrice = createSelector(
    getAuthState,
    (auth) => auth.activePackagePrice,
);

export const getActivePackageKey = createSelector(
    getAuthState,
    (auth) => auth.activePackageKey,
);

export const getActivePackageId = createSelector(
    getAuthState,
    (auth) => auth.activePackageId,
);

export const subscriptionState = (state) => state.auth.subscription || false;

export const getSubscription = createSelector(
    subscriptionState,
    (subscription) => subscription,
);

export const getActivePackge = createSelector(
    getAuthState,
    (auth) => auth.activePackage,
);

export const getPaymentProgress = createSelector(
    getPaymentProgressState,
    (loading) => loading,
);

export const getTokenCreds = createSelector(
    getAuthState,
    (auth) => auth.tokenCreds,
);

export const getSignUpInProgress = (state) =>
    state.auth.signUpInProgress || false;

export const getUser = createSelector(getAuthState, (auth) => auth.user);
export const getUserRole = createSelector(getUser, (user) => user.role);
export const getProfileCompleteStatus = createSelector(
    getAuthState,
    (auth) => auth.hasCompletedProfile,
);
export const getPartnerDetails = createSelector(
    getAuthState,
    (auth) => auth.getPartnerDetails,
);
export const getPartnerLicenseState = createSelector(
    getAuthState,
    (auth) => auth.partnerLicense,
);

/**
 * Get current active role permissions
 */
export const getUserRolePermissions = createSelector(
    getUser,
    (userState) => userState.rolePermissions,
);

/**
 * gets current user's permission
 */
export const getUserPermissions = createSelector(getUser, (userState) => {
    return userState.rolePermissions.permissions || [];
});

/**
 * gets user's current profile picture / name based on current role
 */
export const getCurrentProfilePicture = createSelector(getUser, (userState) => {
    const { rolePermissions, partner } = userState;
    const { key } = rolePermissions;
    return key == 'partner'
        ? partner && partner.profilePicture
        : userState
        ? userState.profilePicture
        : DefaultPic;
});

export const getCurrentUsername = createSelector(getUser, (userState) => {
    const { rolePermissions, partner } = userState;
    const { key } = rolePermissions;
    const isPartnerSelected = key == 'partner';
    return isPartnerSelected
        ? partner && partner.name
        : userState
        ? userState.fullName
        : '';
});

export const getUserData = createSelector(getUser, (userState) => {
    const { id, role, rolePermissions, partner } = userState;
    return {
        userId: id,
        userRole: role,
        roleId: rolePermissions.roleId,
        partnerId: partner ? partner.id : null,
    };
});

/**
 * gets current user's partner profile if exists
 */
export const getPartnerProfile = createSelector(
    getUser,
    (userState) => userState.partner,
);

export const getPartnerStatus = createSelector(
    getUser,
    (userState) => userState.isPartner,
);

/**
 * gets transformed permission into an array of keys
 */
export const getUserPermissionsKeys = createSelector(
    getUserPermissions,
    (permissions) => transformPermissionsKeysToArray(permissions),
);
export const getUserPermissionState = createSelector(
    getAuthState,
    (authState) => authState.fetchingPermissions,
);

/**
 * Get all roles of the current user
 */
export const getUserRoles = createSelector(getUser, (user) => user.roles);

export const getUserEmail = createSelector(getUser, (user) => user.email);

export const getUserPayment = createSelector(getUser, (user) => user.payment);

/**
 * Get current active role of user
 */
export const getUserPermissionRoleId = createSelector(
    getUser,
    (userState) => userState.rolePermissions.roleId,
);

/**
 * Get current requested role of user to switch to
 */
export const getRequestedRoleId = createSelector(
    getAuthState,
    (authState) => authState.requestedRole,
);

export const getPartnerSignupDetails = createSelector(
    getAuthState,
    (auth) => auth.partnerDetails,
);

export const getPartnerSignupError = createSelector(
    getAuthState,
    (auth) => auth.partnerSignUpError,
);

export const getPartnerSignupStatus = createSelector(
    getAuthState,
    (auth) => auth.isPartnerSignedup,
);
/**
 * Get email for reseting the new password
 */
export const getForgotPasswordLoading = createSelector(
    getAuthState,
    (auth) => auth.forgetPasswordLoading,
);
export const getForgotPasswordError = createSelector(
    getAuthState,
    (auth) => auth.forgotPasswordError,
);
export const getForgotPasswordSuccess = createSelector(
    getAuthState,
    (auth) => auth.forgotPasswordSuccessfull,
);
export const getForgotPasswordEmail = createSelector(
    getAuthState,
    (authState) => authState.forgotPasswordEmail,
);

/**
 * Get verification code for reseting the new password
 */
export const getVerificationCodeStatus = createSelector(
    getAuthState,
    (auth) => auth.verificationCodeSet,
);

export const getVerificationCode = createSelector(
    getAuthState,
    (auth) => auth.verificationCode,
);

export const getResetPasswordLoading = createSelector(
    getAuthState,
    (auth) => auth.resettingPassword,
);
export const getResetPasswordError = createSelector(
    getAuthState,
    (auth) => auth.resetPasswordError,
);
export const getResetPasswordSuccess = createSelector(
    getAuthState,
    (auth) => auth.resetPasswordSuccessfull,
);
export const getPriceLoading = createSelector(
    getAuthState,
    (auth) => auth.loadingPrice,
);
export const getPackagePricing = createSelector(getAuthState, (auth) =>
    transformArrayToObject(auth.pricing),
);

export const getPartnerList = createSelector(
    getAuthState,
    (auth) => auth.partnerList,
);

export const getPartnerListLoading = createSelector(
    getAuthState,
    (auth) => auth.loadingPartnerList,
);

export const getHasMorePartners = createSelector(
    getAuthState,
    (auth) => auth.hasMorePartners,
);

export const getPartnerListError = createSelector(
    getAuthState,
    (auth) => auth.partnerListError,
);

export const getViewAsPartner = createSelector(
    getAuthState,
    (auth) => auth.viewAsPartner,
);

export const getSelectedPartner = createSelector(
    getAuthState,
    (auth) => auth.selectedPartner,
);

export const getRefreshingToken = createSelector(
    getAuthState,
    (auth) => auth.refreshingToken,
);

export const getLimitValue = createSelector(getAuthState, (auth) => auth.limit);

export const getSkipValue = createSelector(getAuthState, (auth) => auth.skip);
